import { apiService } from './api';

export interface Hunt {
  id: string;
  title: string;
  description: string;
  creatorId: string;
  difficulty: string;
  estimatedDuration: number;
  status: string;
  centerPoint: {
    latitude: number;
    longitude: number;
  };
  radius: number;
  locations: HuntLocation[];
  createdAt: string;
  completionCount: number;
  averageRating: number;
  category?: string;
}

export interface HuntLocation {
  id: string;
  huntId: string;
  locationId: string;
  orderIndex: number;
  locationData?: {
    name: string;
    description: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface Waypoint {
  waypoint_id?: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  order: number;
}

export interface CreateHuntInput {
  title: string;
  description: string;
  category?: string;
  difficulty: string;
  estimated_duration: number;
  image_url?: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  waypoints: Waypoint[];
}

export interface SearchHuntsParams {
  lat?: number;
  lng?: number;
  radius?: number;
  limit?: number;
  offset?: number;
}

export interface HuntResponse {
  success: boolean;
  data: {
    hunts: Hunt[];
    count: number;
  };
}

class HuntService {
  // Get all hunts
  async getAllHunts(params?: SearchHuntsParams): Promise<Hunt[]> {
    const response = await apiService.post<HuntResponse>('/hunts/search', params || {});
    return response.data.hunts;
  }

  // Get hunt by ID
  async getHunt(huntId: string): Promise<Hunt> {
    const response = await apiService.get<{ success: boolean; hunt: Hunt }>(`/hunts/${huntId}`);
    return response.hunt;
  }

  // Create new hunt
  async createHunt(hunt: CreateHuntInput): Promise<Hunt> {
    const response = await apiService.post<{ success: boolean; data: Hunt }>('/hunts', hunt);
    return response.data;
  }

  // Update hunt
  async updateHunt(huntId: string, hunt: Partial<CreateHuntInput>): Promise<Hunt> {
    const response = await apiService.put<{ success: boolean; data: Hunt }>(`/hunts/${huntId}`, hunt);
    return response.data;
  }

  // Delete hunt
  async deleteHunt(huntId: string): Promise<void> {
    await apiService.delete(`/hunts/${huntId}`);
  }

  // Update hunt status
  async updateHuntStatus(huntId: string, status: string): Promise<Hunt> {
    const response = await apiService.put<{ success: boolean; data: Hunt }>(`/hunts/${huntId}/status`, { status });
    return response.data;
  }
}

export const huntService = new HuntService();
